import { ListType, ObjectType, Type, TypeKind, TypeReference, UnionType } from '../types.js';
import { PropertyPath } from '../engine.js';
import { Schema } from '../schema.js';


export class TypeService {
  private type: Type;
  private currentType: Type;

  constructor(
    typeRef: TypeReference,
    private schema: Schema) {
    this.type = schema.getType(typeRef);
    this.currentType = this.type;
  }

  static getPropertyName(propertyPath: PropertyPath): string {
    const allParts = propertyPath.split('.')
    return allParts[allParts.length - 1];
  }

  getPropertyType(propertyType: PropertyPath): Type {
    const names = propertyType.split('.');
    this.currentType = this.type;
    for (let name of names) {
      this.calculateTypeOneLevelDeep(name);
    }
    return this.currentType;
  }

  findTypeInPropertyPath(propertyType: PropertyPath, predicate: (type: Type, parentType: Type, name: string, path: PropertyPath) => boolean): Type | undefined {
    const names = propertyType.split('.');
    this.currentType = this.type;
    let currentName = '$';
    let prevType = null;
    if (predicate(this.currentType, prevType, '', currentName)) {
      return this.currentType;
    }
    for (let name of names) {
      currentName = currentName + '.' + name;
      prevType = this.currentType;
      this.calculateTypeOneLevelDeep(name);
      if (predicate(this.currentType, prevType, name, currentName)) {
        return this.currentType;
      }
    }
    return undefined;
  }

  private calculateTypeOneLevelDeep(propertyPath: PropertyPath) {
    if (propertyPath === '' || propertyPath === '$') {
      return;
    }
    switch (this.currentType.kind) {
      case TypeKind.Primitive: {
        this.throwError(propertyPath, `Primitive types don't have inner properties`);
        break;
      }

      case TypeKind.List: {
        const index = +propertyPath;
        if (Number.isFinite(index)) {
          const list = this.type as ListType;
          this.currentType = this.schema.getType(list.elementType);
          return;
        } else {
          this.throwError(propertyPath, `Lists require indexes to be used as a property name`)
          break;
        }
      }
      case TypeKind.Enum: {
        this.throwError(propertyPath, `Enum types don't have inner properties`);
        break;
      }
      case TypeKind.Object: {
        const obj = this.currentType as ObjectType;
        const property = obj.properties.find(p => p.name === propertyPath);
        if (property) {
          this.currentType = this.schema.getType(property.type);
          return;
        } else {
          const externalProp = obj.externals.find(e => e.name === propertyPath);
          if (externalProp) {
            this.currentType = this.schema.getType(externalProp.type);
            return;
          } else
            this.throwError(propertyPath);
          break;
        }
      }
      case TypeKind.Union: {
        const union = this.currentType as UnionType;
        const typesWithProperties: Type[] = [];
        union.types.forEach(typeRef => {
          const type = this.schema.getType(typeRef);
          this.currentType = type;
          try {
            this.calculateTypeOneLevelDeep(propertyPath);
            const innerType = this.currentType;
            if (!typesWithProperties.find(t => t.getTypeReference() === innerType.getTypeReference())) {
              typesWithProperties.push(innerType);
            }
          } catch (e) {
            // Skip types that doesn't have needed property
          }
        });
        if (typesWithProperties.length > 0) {
          if (typesWithProperties.length === 1) {
            this.currentType = typesWithProperties[0];
            return;
          } else {
            const typeNames = typesWithProperties.map(t => t.getTypeReference());
            this.currentType = new UnionType(typeNames.join('|'), typeNames);
            return;
          }
        } else {
          this.throwError('None of the branches of Union has a type with specified name');
          break;
        }
      }
    }
  }

  private throwError(propertyPath: PropertyPath, message: string = ''): never {
    throw new Error(`Cannot get type of ${propertyPath} property of ${this.currentType}. ${message}`);
  }

  static getParentChildPath(propertyPath: PropertyPath): { parent: PropertyPath, child: PropertyPath } {
    const pathes = propertyPath.split('.');
    const parent = pathes.slice(0, -1).join('.');
    const child = pathes[pathes.length - 1];
    return {
      parent, child
    }
  }

  isPropertyExternal(columnName: string) {
    if (this.type.kind == TypeKind.Object) {
      const objectType = this.type as ObjectType;
      return objectType.externals.some(external => external.name === columnName);
    }
    return false;
  }

  static absolutePathToRelative(absolutePath: PropertyPath) {
    if (absolutePath.startsWith('$.')) {
      return absolutePath.slice(2)
    } else {
      return absolutePath;
    }
  }
  static joinPath(path: PropertyPath, path2: PropertyPath) {
    return path + '.' + path2;
  }
}
